import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, Upload, Minimize2, Maximize2, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const CssMinifierTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'minify' | 'beautify'>('minify');
  const [compressionLevel, setCompressionLevel] = useState<'basic' | 'advanced'>('basic');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const minifyCss = (css: string, level: 'basic' | 'advanced' = 'basic') => {
    let minified = css;

    // Remove comments
    minified = minified.replace(/\/\*[\s\S]*?\*\//g, '');

    // Remove unnecessary whitespace
    minified = minified.replace(/\s+/g, ' ');
    minified = minified.replace(/\s*{\s*/g, '{');
    minified = minified.replace(/;\s*/g, ';');
    minified = minified.replace(/\s*}\s*/g, '}');
    minified = minified.replace(/\s*,\s*/g, ',');
    minified = minified.replace(/\s*:\s*/g, ':');
    minified = minified.replace(/\s*>\s*/g, '>');
    minified = minified.replace(/\s*\+\s*/g, '+');
    minified = minified.replace(/\s*~\s*/g, '~');

    // Remove trailing semicolons before closing braces
    minified = minified.replace(/;}/g, '}');

    // Remove leading/trailing whitespace
    minified = minified.trim();

    if (level === 'advanced') {
      // Advanced optimizations
      
      // Convert hex colors to shorter forms
      minified = minified.replace(/#([a-fA-F0-9])\1([a-fA-F0-9])\2([a-fA-F0-9])\3/g, '#$1$2$3');
      
      // Remove unnecessary zeros
      minified = minified.replace(/0\.(\d+)/g, '.$1');
      minified = minified.replace(/(\d)\.0+(?=\D)/g, '$1');
      
      // Remove unnecessary units for zero values
      minified = minified.replace(/\b0+(px|em|rem|%|pt|pc|in|cm|mm|ex|ch|vw|vh|vmin|vmax|deg|rad|turn|s|ms|Hz|kHz|dpi|dpcm|dppx)\b/g, '0');
      
      // Simplify margin/padding shorthand
      minified = minified.replace(/margin:0 0 0 0/g, 'margin:0');
      minified = minified.replace(/padding:0 0 0 0/g, 'padding:0');
      
      // Convert named colors to shorter hex values where possible
      const colorMap: { [key: string]: string } = {
        'white': '#fff',
        'black': '#000',
        'red': '#f00',
        'green': '#008000',
        'blue': '#00f',
        'yellow': '#ff0',
        'cyan': '#0ff',
        'magenta': '#f0f'
      };
      
      Object.entries(colorMap).forEach(([name, hex]) => {
        const regex = new RegExp(`\\b${name}\\b`, 'gi');
        minified = minified.replace(regex, hex);
      });
    }

    setOutput(minified);
    return minified;
  };

  const beautifyCss = (css: string) => {
    let beautified = css;

    // Add proper spacing around braces
    beautified = beautified.replace(/\{/g, ' {\n  ');
    beautified = beautified.replace(/\}/g, '\n}\n');
    beautified = beautified.replace(/;/g, ';\n  ');
    beautified = beautified.replace(/,/g, ',\n');

    // Fix indentation
    const lines = beautified.split('\n');
    let indentLevel = 0;
    const indentedLines = lines.map(line => {
      const trimmed = line.trim();
      if (trimmed === '') return '';
      
      if (trimmed.includes('}')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      const indented = '  '.repeat(indentLevel) + trimmed;
      
      if (trimmed.includes('{')) {
        indentLevel++;
      }
      
      return indented;
    });

    beautified = indentedLines.join('\n');
    
    // Clean up extra newlines
    beautified = beautified.replace(/\n\s*\n\s*\n/g, '\n\n');
    beautified = beautified.trim();

    setOutput(beautified);
    return beautified;
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    
    if (value.trim()) {
      if (mode === 'minify') {
        minifyCss(value, compressionLevel);
      } else {
        beautifyCss(value);
      }
    } else {
      setOutput('');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadResult = () => {
    if (output) {
      const filename = mode === 'minify' ? t('tools.cssMinifier.minifiedFilename') : t('tools.cssMinifier.beautifiedFilename');
      const blob = new Blob([output], { type: 'text/css' });
      saveAs(blob, filename);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        if (mode === 'minify') {
          minifyCss(content, compressionLevel);
        } else {
          beautifyCss(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  const sampleCss = `/* Sample CSS */
.header {
    background-color: #ffffff;
    padding: 20px 0px 20px 0px;
    margin: 0px;
    border: 1px solid #cccccc;
}

.navigation ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.navigation li {
    display: inline-block;
    margin-right: 20px;
}

.content {
    background: white;
    padding: 1.0em;
    margin: 0.0rem;
}`;

  const useSample = () => {
    setInput(sampleCss);
    if (mode === 'minify') {
      minifyCss(sampleCss, compressionLevel);
    } else {
      beautifyCss(sampleCss);
    }
  };

  const compressionStats = () => {
    if (!input || !output) return null;
    
    const originalSize = new Blob([input]).size;
    const compressedSize = new Blob([output]).size;
    const savings = originalSize - compressedSize;
    const percentage = ((savings / originalSize) * 100).toFixed(1);
    
    return {
      originalSize,
      compressedSize,
      savings,
      percentage: mode === 'minify' ? percentage : '0'
    };
  };

  const stats = compressionStats();

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>{t('tools.cssMinifier.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => {
                setMode('minify');
                if (input) minifyCss(input, compressionLevel);
              }}
              variant={mode === 'minify' ? 'primary' : 'secondary'}
            >
              <Minimize2 className="w-4 h-4 mr-2" />
              {t('tools.cssMinifier.minify')}
            </Button>
            <Button
              onClick={() => {
                setMode('beautify');
                if (input) beautifyCss(input);
              }}
              variant={mode === 'beautify' ? 'primary' : 'secondary'}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              {t('tools.cssMinifier.beautify')}
            </Button>
            
            {mode === 'minify' && (
              <>
                <Button
                  onClick={() => {
                    setCompressionLevel('basic');
                    if (input) minifyCss(input, 'basic');
                  }}
                  variant={compressionLevel === 'basic' ? 'primary' : 'outline'}
                  size="sm"
                >
                  {t('tools.cssMinifier.basic')}
                </Button>
                <Button
                  onClick={() => {
                    setCompressionLevel('advanced');
                    if (input) minifyCss(input, 'advanced');
                  }}
                  variant={compressionLevel === 'advanced' ? 'primary' : 'outline'}
                  size="sm"
                >
                  {t('tools.cssMinifier.advanced')}
                </Button>
              </>
            )}
            
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={downloadResult}
              disabled={!output}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={useSample}
              variant="outline"
            >
              {t('tools.cssMinifier.useSample')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".css"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.cssMinifier.cssInput')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={t('tools.cssMinifier.inputPlaceholder')}
              className="min-h-[400px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{mode === 'minify' ? t('tools.cssMinifier.minifiedCss') : t('tools.cssMinifier.beautifiedCss')}</span>
              {output && (
                <span className="text-sm text-gray-500">
                  {output.split('\n').length} {t('tools.cssMinifier.lines')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder={mode === 'minify' ? t('tools.cssMinifier.minifiedPlaceholder') : t('tools.cssMinifier.beautifiedPlaceholder')}
              className="min-h-[400px] font-mono text-sm bg-gray-50 dark:bg-gray-800"
            />
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>Compression Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Original Size:</span>
                <div className="font-mono">{(stats.originalSize / 1024).toFixed(2)} KB</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Output Size:</span>
                <div className="font-mono">{(stats.compressedSize / 1024).toFixed(2)} KB</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Size Reduction:</span>
                <div className="font-mono">{(stats.savings / 1024).toFixed(2)} KB</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Compression:</span>
                <div className="font-mono text-green-600">{stats.percentage}%</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
